import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, PlusCircle, Image, Calendar, Send, Palette,
  Pin, ChevronRight, Loader2, Settings, ArrowLeft, LayoutGrid
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StudioOverview from '../components/pinterest-studio/StudioOverview';
import PinCreator from '../components/pinterest-studio/PinCreator';
import PinEditor from '../components/pinterest-studio/PinEditor';
import PinGallery from '../components/pinterest-studio/PinGallery';
import BrandKitPanel from '../components/pinterest-studio/BrandKitPanel';
import CampaignSetup from '../components/pinterest-studio/CampaignSetup';
import CampaignReview from '../components/pinterest-studio/CampaignReview';
import CampaignDashboard from '../components/pinterest-studio/CampaignDashboard';
import CampaignList from '../components/pinterest-studio/CampaignList';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const NAV = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'create', label: 'Create Pins', icon: PlusCircle },
  { id: 'campaigns', label: 'Campaigns', icon: LayoutGrid },
  { id: 'pins', label: 'My Pins', icon: Image },
  { id: 'scheduled', label: 'Scheduled', icon: Calendar },
  { id: 'published', label: 'Published', icon: Send },
  { id: 'brand', label: 'Brand Kit', icon: Palette },
];

const PinterestStudio = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const section = params.get('tab') || 'overview';
  const editPinId = params.get('edit');
  const campaignId = params.get('campaign');
  const campaignAction = params.get('action'); // 'new' | 'review' | 'view'

  const [stats, setStats] = useState(null);
  const [pinterestStatus, setPinterestStatus] = useState(null);
  const [brandKit, setBrandKit] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, pRes, bkRes] = await Promise.all([
        axios.get(`${API}/api/pin-studio/stats`, getAuth()),
        axios.get(`${API}/api/pinterest/status`, getAuth()).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/api/brand-kit`, getAuth()),
      ]);
      setStats(statsRes.data);
      setPinterestStatus(pRes.data);
      setBrandKit(bkRes.data);
    } catch (e) {
      console.error('Studio init error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  const setSection = (id) => {
    const p = new URLSearchParams();
    p.set('tab', id);
    setParams(p);
  };

  const openEditor = (pinId) => {
    const p = new URLSearchParams();
    p.set('tab', 'create');
    p.set('edit', pinId);
    setParams(p);
  };

  // If editing a pin, show the editor
  if (editPinId) {
    return (
      <PinEditor
        pinId={editPinId}
        brandKit={brandKit}
        pinterestStatus={pinterestStatus}
        onBack={() => {
          const p = new URLSearchParams(params);
          p.delete('edit');
          if (!p.get('tab')) p.set('tab', 'pins');
          setParams(p);
        }}
        onPublished={() => fetchInitial()}
      />
    );
  }

  // Campaign sub-routes
  if (section === 'campaigns' && campaignId && campaignAction === 'review') {
    return (
      <div className="min-h-screen bg-[#F7F5F2]" data-testid="pinterest-studio">
        <div className="bg-white border-b border-stone-100 sticky top-0 z-30">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 rounded-lg hover:bg-stone-50 transition-colors">
              <ArrowLeft className="w-4 h-4 text-stone-400" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#E60023] flex items-center justify-center">
                <Pin className="w-3.5 h-3.5 text-white" />
              </div>
              <h1 className="font-serif font-bold text-stone-800 text-base sm:text-lg">Kampanje Review</h1>
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          <CampaignReview
            campaignId={campaignId}
            onBack={() => { const p = new URLSearchParams(); p.set('tab', 'campaigns'); setParams(p); }}
            onEditPin={openEditor}
            onApproved={() => {
              const p = new URLSearchParams();
              p.set('tab', 'campaigns');
              p.set('campaign', campaignId);
              p.set('action', 'view');
              setParams(p);
            }}
          />
        </div>
      </div>
    );
  }

  if (section === 'campaigns' && campaignId && campaignAction === 'view') {
    return (
      <div className="min-h-screen bg-[#F7F5F2]" data-testid="pinterest-studio">
        <div className="bg-white border-b border-stone-100 sticky top-0 z-30">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 rounded-lg hover:bg-stone-50 transition-colors">
              <ArrowLeft className="w-4 h-4 text-stone-400" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#E60023] flex items-center justify-center">
                <Pin className="w-3.5 h-3.5 text-white" />
              </div>
              <h1 className="font-serif font-bold text-stone-800 text-base sm:text-lg">Kampanje</h1>
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          <CampaignDashboard
            campaignId={campaignId}
            onBack={() => { const p = new URLSearchParams(); p.set('tab', 'campaigns'); setParams(p); }}
            onEditCampaign={(id) => {
              const p = new URLSearchParams();
              p.set('tab', 'campaigns');
              p.set('campaign', id);
              p.set('action', 'review');
              setParams(p);
            }}
            onEditPin={openEditor}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#B49A88]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="pinterest-studio">
      {/* Top bar */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 rounded-lg hover:bg-stone-50 transition-colors" data-testid="studio-back-btn">
              <ArrowLeft className="w-4 h-4 text-stone-400" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#E60023] flex items-center justify-center">
                <Pin className="w-3.5 h-3.5 text-white" />
              </div>
              <h1 className="font-serif font-bold text-stone-800 text-base sm:text-lg">Pinterest Studio</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pinterestStatus?.connected ? (
              <span className="text-[10px] font-sans font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full" data-testid="studio-connected-badge">
                @{pinterestStatus.username}
              </span>
            ) : (
              <span className="text-[10px] font-sans font-medium text-stone-400 bg-stone-50 px-2.5 py-1 rounded-full" data-testid="studio-disconnected-badge">
                Not connected
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar nav */}
          <nav className="hidden md:flex flex-col gap-1 w-52 flex-shrink-0" data-testid="studio-nav">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`studio-nav-${n.id}`}
                onClick={() => setSection(n.id)}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-sans font-medium transition-all ${
                  section === n.id
                    ? 'bg-white text-stone-800 shadow-sm border border-stone-100'
                    : 'text-stone-500 hover:text-stone-700 hover:bg-white/50'
                }`}
              >
                <n.icon className={`w-4 h-4 ${section === n.id ? 'text-[#E60023]' : ''}`} />
                {n.label}
                {n.id === 'scheduled' && stats?.scheduled > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-[#E60023]/10 text-[#E60023] px-1.5 py-0.5 rounded-full">{stats.scheduled}</span>
                )}
                {n.id === 'pins' && stats?.drafts > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded-full">{stats.drafts}</span>
                )}
              </button>
            ))}
          </nav>

          {/* Mobile nav */}
          <div className="md:hidden flex gap-1 overflow-x-auto pb-3 -mx-4 px-4 scrollbar-hide mb-2">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => setSection(n.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-sans font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  section === n.id
                    ? 'bg-white text-stone-800 shadow-sm border border-stone-100'
                    : 'text-stone-500 hover:bg-white/50'
                }`}
              >
                <n.icon className="w-3.5 h-3.5" />
                {n.label}
              </button>
            ))}
          </div>

          {/* Content area */}
          <main className="flex-1 min-w-0">
            {section === 'overview' && (
              <StudioOverview
                stats={stats}
                pinterestStatus={pinterestStatus}
                brandKit={brandKit}
                onNavigate={setSection}
                onEditPin={openEditor}
                onRefresh={fetchInitial}
              />
            )}
            {section === 'create' && (
              <PinCreator
                brandKit={brandKit}
                pinterestStatus={pinterestStatus}
                onPinsCreated={() => { fetchInitial(); setSection('pins'); }}
                onEditPin={openEditor}
              />
            )}
            {section === 'campaigns' && !campaignId && campaignAction !== 'new' && (
              <CampaignList
                onCreateNew={() => {
                  const p = new URLSearchParams();
                  p.set('tab', 'campaigns');
                  p.set('action', 'new');
                  setParams(p);
                }}
                onOpenCampaign={(id) => {
                  const p = new URLSearchParams();
                  p.set('tab', 'campaigns');
                  p.set('campaign', id);
                  p.set('action', 'view');
                  setParams(p);
                }}
                onReviewCampaign={(id) => {
                  const p = new URLSearchParams();
                  p.set('tab', 'campaigns');
                  p.set('campaign', id);
                  p.set('action', 'review');
                  setParams(p);
                }}
              />
            )}
            {section === 'campaigns' && campaignAction === 'new' && (
              <CampaignSetup
                brandKit={brandKit}
                onCampaignCreated={(id, data) => {
                  const p = new URLSearchParams();
                  p.set('tab', 'campaigns');
                  p.set('campaign', id);
                  p.set('action', 'review');
                  setParams(p);
                }}
              />
            )}
            {(section === 'pins' || section === 'scheduled' || section === 'published') && (
              <PinGallery
                filter={section === 'pins' ? null : section}
                onEditPin={openEditor}
                onRefresh={fetchInitial}
                pinterestStatus={pinterestStatus}
              />
            )}
            {section === 'brand' && (
              <BrandKitPanel
                brandKit={brandKit}
                onUpdate={(bk) => setBrandKit(bk)}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PinterestStudio;
